import { useEffect, useState } from 'react';
import { pushSupported, getCurrentSubscription, subscribeToPush, sendTest, isIos, isStandalone } from '../lib/push.js';

export default function PushPrompt() {
  const [state, setState] = useState({ supported: false, permission: 'default', subscribed: false, busy: false, err: '', dismissed: false, testSent: false, diag: '' });

  useEffect(() => {
    (async () => {
      const supported = pushSupported();
      const permission = supported && typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
      const sub = supported ? await getCurrentSubscription() : null;
      const dismissed = localStorage.getItem('ks_push_dismissed') === '1';
      const diag = [
        `iOS: ${isIos()}`,
        `Standalone (PWA): ${isStandalone()}`,
        `serviceWorker: ${'serviceWorker' in navigator}`,
        `PushManager: ${'PushManager' in window}`,
        `Notification: ${typeof Notification !== 'undefined'}`,
        `permission: ${permission}`,
        `subscribed: ${!!sub}`,
      ].join(' · ');
      setState(s => ({ ...s, supported, permission, subscribed: !!sub, dismissed, diag }));
    })();
  }, []);

  async function enable() {
    setState(s => ({ ...s, busy: true, err: '' }));
    try {
      await subscribeToPush();
      setState(s => ({ ...s, busy: false, subscribed: true, permission: 'granted' }));
    } catch (e) {
      setState(s => ({ ...s, busy: false, err: e.message }));
    }
  }

  async function test() {
    setState(s => ({ ...s, busy: true, err: '' }));
    try {
      await sendTest();
      setState(s => ({ ...s, busy: false, testSent: true }));
      setTimeout(() => setState(s => ({ ...s, testSent: false })), 4000);
    } catch (e) {
      setState(s => ({ ...s, busy: false, err: e.message }));
    }
  }

  function dismiss() {
    localStorage.setItem('ks_push_dismissed', '1');
    setState(s => ({ ...s, dismissed: true }));
  }

  if (state.dismissed) return null;

  // Already subscribed — show test-send hint
  if (state.subscribed) {
    return (
      <Banner tone="ok" onDismiss={dismiss}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            ✓ Notifications enabled on this device.
            {state.testSent && <span className="ml-2">Test sent — check your home screen.</span>}
          </div>
          <button onClick={test} disabled={state.busy} className="btn-ghost text-xs py-1 px-2">
            {state.busy ? '…' : 'Send test'}
          </button>
        </div>
        <div className="text-xs text-ink-700/50 mt-1 break-all">{state.diag}</div>
      </Banner>
    );
  }

  // iOS, not yet installed as PWA
  if (isIos() && !isStandalone()) {
    return (
      <Banner tone="info" onDismiss={dismiss}>
        📱 <strong>iPhone setup:</strong> tap <strong>Share</strong> → <strong>Add to Home Screen</strong> → open the app from the home screen, then come back here.
        <div className="text-xs text-ink-700/50 mt-1 break-all">{state.diag}</div>
      </Banner>
    );
  }

  // Browser doesn't support push at all
  if (!state.supported) {
    const insecureLan = typeof window !== 'undefined'
      && window.location.protocol === 'http:'
      && window.location.hostname !== 'localhost'
      && window.location.hostname !== '127.0.0.1';
    return (
      <Banner tone="info" onDismiss={dismiss}>
        ⚠️ Push notifications need a <strong>secure context</strong> (HTTPS).{' '}
        {insecureLan
          ? <>You're on <code className="font-mono text-xs">{window.location.host}</code> over plain HTTP. iOS + Android both block service workers there. Deploy to Fly (HTTPS for free) or run an HTTPS tunnel (e.g. <code className="font-mono text-xs">cloudflared tunnel --url http://localhost:8080</code>).</>
          : (isIos() ? 'iOS 16.4+ in PWA mode is required.' : 'Try Chrome, Safari, or Firefox latest.')}
        <div className="text-xs text-ink-700/50 mt-1 break-all">{state.diag}</div>
      </Banner>
    );
  }

  // Permission was previously denied — user must re-enable in Settings
  if (state.permission === 'denied') {
    return (
      <Banner tone="info" onDismiss={dismiss}>
        🔕 Notifications were blocked previously. {isIos()
          ? 'On iPhone: Settings → Notifications → KingStead → Allow Notifications.'
          : 'Click the lock icon in the address bar → Notifications → Allow.'}
        <div className="text-xs text-ink-700/50 mt-1 break-all">{state.diag}</div>
      </Banner>
    );
  }

  // Default state — Enable button
  return (
    <Banner tone="info" onDismiss={dismiss}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          🔔 Get chore + PM reminders pushed to this device. <span className="text-ink-700/60 text-xs">No SMS, no fees.</span>
        </div>
        <button onClick={enable} disabled={state.busy} className="btn-primary text-sm py-1.5 px-3">
          {state.busy ? 'Enabling…' : 'Enable notifications'}
        </button>
      </div>
      {state.err && <div className="text-rust-700 text-xs mt-1">{state.err}</div>}
      <div className="text-xs text-ink-700/50 mt-1 break-all">{state.diag}</div>
    </Banner>
  );
}

function Banner({ children, tone = 'info', onDismiss }) {
  const cls = tone === 'ok' ? 'bg-olive-100 text-olive-900' : 'bg-gold-500/10 text-ink-900';
  return (
    <div className={`rounded-lg p-3 text-sm ${cls} mb-4`}>
      <div className="flex items-start gap-2">
        <div className="flex-1">{children}</div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-ink-700/60 hover:text-ink-900 text-xs">dismiss</button>
        )}
      </div>
    </div>
  );
}
